import { ReactNode, useEffect, useState } from 'react';

import { Button, Stack, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledLinearProgressBar from 'src/components/StyledLinearProgressBar';
import StyledRadioGroup from 'src/components/StyledRadioGroup';
import TeamMemberAvatar from 'src/components/TeamMemberAvatar';
import { TeamRoles } from 'src/constants/TeamRoles';
import { useOnboardingWorkflow, VisitType } from 'src/contexts/OnboardingWorkflowContext';
import { getTeamMemberDisplayName } from 'src/utils/nameUtils';

export const Route = createFileRoute('/(app)/onboarding/visit-type')({
    component: VisitTypeComponent,
});

const VisitTypeOptions: { value: VisitType; label: ReactNode | string }[] = [
    {
        value: 'repeating',
        label: (
            <>
                <Typography variant="body1">Repeating Visits</Typography>
                <Typography variant="body2">There is a regular weekly schedule for care</Typography>
            </>
        ),
    },
    {
        value: 'single',
        label: (
            <>
                <Typography variant="body1">Single Visit</Typography>
                <Typography variant="body2">
                    There is care scheduled for a specific date and time
                </Typography>
            </>
        ),
    },
];

function VisitTypeComponent() {
    const {
        canGoBack,
        previousStep,
        nextStep,
        skipToShareInvite,
        progress,
        invitedTeamMember,
        visitType,
        setVisitType,
    } = useOnboardingWorkflow();

    const [localVisitType, setLocalVisitType] = useState<VisitType>('repeating');

    const handleVisitTypeChange = (value: string) => {
        setLocalVisitType(value as VisitType);
    };

    const handleNext = () => {
        setVisitType(localVisitType);
        nextStep();
    };

    useEffect(() => {
        if (visitType) {
            setLocalVisitType(visitType);
        }
    }, [visitType]);

    return (
        <>
            <PageHeader
                hideBackButton={!canGoBack}
                onBack={canGoBack ? previousStep : undefined}
                hideActionButton
            >
                <StyledLinearProgressBar value={progress.percentage} />
            </PageHeader>
            <Stack sx={{ gap: 4, mb: 2.5 }}>
                <Stack sx={{ gap: 2 }}>
                    <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                        How will this person provide care?
                    </Typography>
                </Stack>

                {invitedTeamMember && (
                    <Stack sx={{ gap: 2, alignItems: 'center' }}>
                        <TeamMemberAvatar
                            teamMember={invitedTeamMember}
                            isLicensedCaregiver={
                                invitedTeamMember?.role === TeamRoles.LicensedCaregiver
                            }
                            size="large"
                        />
                        <Stack sx={{ alignItems: 'center', textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {getTeamMemberDisplayName(invitedTeamMember, {
                                    initializeLast: true,
                                })}
                            </Typography>
                            <Typography variant="subtitle2">Pending Invitation</Typography>
                        </Stack>
                    </Stack>
                )}

                <StyledRadioGroup
                    options={VisitTypeOptions}
                    value={localVisitType || ''}
                    onChange={handleVisitTypeChange}
                    labelPlacement="start"
                />
            </Stack>
            <PageFooter>
                <Button variant="contained" color="primary" fullWidth onClick={handleNext}>
                    Continue
                </Button>
                <Button variant="text" fullWidth onClick={skipToShareInvite}>
                    Skip for now
                </Button>
            </PageFooter>
        </>
    );
}
